package ai.jessie.web.work

import ai.jessie.shared.work.WorkCalendarResponse
import ai.jessie.shared.work.WorkCalendarView
import ai.jessie.shared.work.WorkSelectionsResponse
import ai.jessie.web.onboarding.OnboardingProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

// The routed range request. An absent view or date is the server's default, never a browser date.
data class WorkCalendarRoute(
    val organisationId: String,
    val view: WorkCalendarView? = null,
    val date: String? = null,
)

data class WorkCalendarUiState(
    val calendar: WorkCalendarResponse? = null,
    val selections: WorkSelectionsResponse? = null,
    val selection: WorkCalendarSelection = WorkCalendarSelection(),
    val selectionProblem: String? = null,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val notice: String? = null,
)

data class WorkCalendarActions(
    val refresh: () -> Unit,
    val select: ((WorkCalendarSelection) -> WorkCalendarSelection) -> Unit,
    val createFromSelection: () -> Unit,
    val createOn: (String) -> Unit,
    val open: (String) -> Unit,
    val back: () -> Unit,
)

class WorkCalendarPorts(val calendar: WorkCalendarPort, val work: WorkPort)

/**
 * UC-009. One state per routed range, single-flight; a disposed page scope cannot publish a late
 * answer, so another range or organisation never shows the previous one. A failed load clears the
 * calendar instead of keeping entries or totals that may be incomplete (A8, A9). Creation and
 * editing run in an embedded UC-008 [WorkState], so validation, revisions and denials are unchanged
 * (BR-048).
 */
class WorkCalendarState(
    private val ports: WorkCalendarPorts,
    private val scope: CoroutineScope,
    private val route: WorkCalendarRoute,
) {
    var state by mutableStateOf(WorkCalendarUiState())
        private set

    var editor by mutableStateOf<WorkState?>(null)
        private set

    val actions =
        WorkCalendarActions(
            refresh = { load() },
            select = { update ->
                if (!state.busy)
                    state = state.copy(selection = update(state.selection), selectionProblem = null)
            },
            createFromSelection = ::createFromSelection,
            // A1, BR-050: a month-view date prefills the date only; the duration is entered.
            createOn = { date -> if (creatable(date)) edit(null, WorkPreset(date)) },
            // A2: only an entry the server returned for this range can be opened.
            open = { workId ->
                val shown = state.calendar?.days.orEmpty().flatMap { it.entries }
                if (shown.any { it.id == workId }) edit(workId, null)
            },
            back = { if (editor?.state?.busy != true) close(null) },
        )

    // Steps 2–4: the range, its entries and totals, with the choices that name their references.
    fun load(notice: String? = null) {
        if (state.busy || editor != null) return
        state = state.copy(busy = true, error = null, notice = notice, selectionProblem = null)
        scope.launch {
            try {
                val selections = ports.work.selections(route.organisationId)
                val calendar = ports.calendar.calendar(route.organisationId, route.view, route.date)
                currentCoroutineContext().ensureActive()
                state = state.copy(calendar = calendar, selections = selections)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: WorkRequestFailure) {
                currentCoroutineContext().ensureActive()
                state = WorkCalendarUiState(busy = true, error = failure.calendarProblem())
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                state = WorkCalendarUiState(busy = true, error = calendarUnavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    /**
     * Step 11, A2 and A7: a stored or cancelled entry returns to the calendar, which is loaded
     * again so entries and totals are the server's. Anything else stays in the editor.
     */
    fun settle(work: WorkUiState) {
        val settled = !work.busy && work.draft == null
        if (editor != null && settled && work.notice != null) close(work.notice)
    }

    // A4: without a positive same-date period the form does not open.
    private fun createFromSelection() {
        val selection = state.selection
        val minutes = selection.snappedMinutes()
        val week = state.calendar?.view == WorkCalendarView.WEEK
        if (!week || !creatable(selection.date) || minutes == null) {
            if (!state.busy) state = state.copy(selectionProblem = SELECTION_REQUIREMENT)
            return
        }
        edit(null, WorkPreset(selection.date, minutes))
    }

    private fun creatable(date: String) = state.calendar?.creatableDates().orEmpty().contains(date)

    private fun edit(workId: String?, preset: WorkPreset?) {
        if (state.busy || editor != null) return
        state = state.copy(selection = WorkCalendarSelection(), selectionProblem = null)
        editor =
            WorkState(ports.work, scope, route.organisationId, workId, preset).also {
                if (workId == null) it.actions.create() else it.refresh()
            }
    }

    private fun close(notice: String?) {
        editor = null
        load(notice)
    }
}

internal const val SELECTION_REQUIREMENT =
    "Das Formular wurde nicht geöffnet. Wähle einen Tag sowie einen Beginn und ein späteres Ende " +
        "am selben Tag; die Zeiten werden auf 5 Minuten gerundet."
